import React from 'react';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Button,
  alpha,
} from '@mui/material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../contexts/AuthContext';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PeopleIcon from '@mui/icons-material/People';
import SpaIcon from '@mui/icons-material/Spa';

const DRAWER_WIDTH = 260;

interface NavItem {
  text: string;
  path: string;
  icon: React.ReactNode;
  parent?: string;
}

const navItems: NavItem[] = [
  { text: 'Dashboard', path: '/', icon: <DashboardIcon fontSize="small" /> },
  { text: 'Kullanıcılar', path: '/users', icon: <PeopleIcon fontSize="small" /> },
  { text: 'Tüm Ürünler', path: '/products', icon: <InventoryIcon fontSize="small" /> },
  { text: 'Bekleyen Ürünler', path: '/pending-products', icon: <HourglassEmptyIcon fontSize="small" /> },
  { text: 'JSON Ürünler', path: '/products-json', icon: <ListAltIcon fontSize="small" />, parent: 'Bekleyen Ürünler' },
];

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const currentPage = navItems.find((item) => item.path === location.pathname)
    ?? (location.pathname.startsWith('/users/') ? { text: 'Kullanıcı Detayı' } : undefined);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            bgcolor: '#0F172A',
            border: 'none',
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ px: 2.5, py: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: alpha('#10B981', 0.15),
                  color: '#10B981',
                  flexShrink: 0,
                }}
              >
                <SpaIcon />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2, color: '#F8FAFC' }} noWrap>
                  Zamanında Tüket
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B' }}>
                  Admin Panel
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ borderColor: alpha('#FFFFFF', 0.08) }} />

          <List sx={{ px: 1.5, py: 2, flex: 1 }}>
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <ListItemButton
                  key={item.path}
                  selected={isActive}
                  onClick={() => navigate(item.path)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    pl: item.parent ? 4 : 2,
                    py: 1.25,
                    color: isActive ? '#FFFFFF' : '#94A3B8',
                    '&.Mui-selected': {
                      bgcolor: alpha('#10B981', 0.15),
                      color: '#10B981',
                      '&:hover': { bgcolor: alpha('#10B981', 0.2) },
                      '& .MuiListItemIcon-root': { color: '#10B981' },
                    },
                    '&:hover': {
                      bgcolor: alpha('#FFFFFF', 0.05),
                      color: '#E2E8F0',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: isActive ? 600 : 500,
                      noWrap: true,
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>

          <Box sx={{ px: 2.5, py: 2.5, borderTop: `1px solid ${alpha('#FFFFFF', 0.08)}` }}>
            {user?.email && (
              <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', mb: 1 }} noWrap>
                {user.email}
              </Typography>
            )}
            <Button
              fullWidth
              size="small"
              startIcon={<LogoutIcon fontSize="small" />}
              onClick={async () => {
                await logout();
                navigate('/login');
              }}
              sx={{
                justifyContent: 'flex-start',
                color: '#94A3B8',
                mb: 1.5,
                '&:hover': { bgcolor: alpha('#FFFFFF', 0.05), color: '#E2E8F0' },
              }}
            >
              Çıkış Yap
            </Button>
            <Typography variant="caption" sx={{ color: '#64748B' }}>
              v0.1.0 · Zamanında Tüket
            </Typography>
          </Box>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 4,
            py: 2.5,
            minHeight: 56,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            flexShrink: 0,
          }}
        >
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {currentPage?.text || 'Admin Panel'}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            p: 4,
            width: '100%',
            boxSizing: 'border-box',
            overflowX: 'auto',
          }}
        >
          <Box sx={{ width: '100%', maxWidth: '100%' }}>
            <Outlet />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
